'use client';

import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
  Cog6ToothIcon, PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { Anchor, TailwindSpinner, MissionControlSwitch } from '@components/common';
import { useSwrJobSearchAlerts } from '@hooks/useSwrApi/endpoints/job-search-alert';
import { useSwrOwnResumeDetail } from '@hooks/useSwrApi/endpoints/resume';
import { useSidebarNavigation, CandidateSearchFilters } from '@components/contexts/SidebarNavigationContext';
import { slugify } from '@utils/strings/slugify';
import { getI18nLinkHref } from '@services/i18nPaths';
import type { JobSearchQueryParams } from '@type/filters';
import { MemoizedCompactJobAlertCard } from './CompactJobAlertCard';
import {
  MC_CARD_TYPE_ALERT,
  MC_CARD_TYPE_LAST_EXPERIENCE,
  MC_CARD_TYPE_NEXT_JOB,
  MC_KEYWORD_SOURCE_LAST_EXPERIENCE,
  MC_KEYWORD_SOURCE_NEXT_JOB,
  type McCardType,
  type McKeywordSource,
} from './cardTypes';
import { POPULAR_SEARCH_KEYS } from './popularSearchKeys';
import { GuestJobsSidebar } from './GuestJobsSidebar';

const ALERTS_MAX = 3; // Show only first 3 alerts for screen space

interface CandidateJobsSidebarProps {
  locale: Locale;
  className?: string;
  isAuthenticated?: boolean;
  showAccountSettings?: boolean;
  onSwitchContext?: () => void;
  /** Opens the Jobs on Map modal. Provided by MissionControl to avoid import cycles. */
  onOpenMap?: (queryParams?: Partial<JobSearchQueryParams>) => void;
}

/**
 * @description Inner component that displays job alerts for authenticated candidates.
 */
const CandidateJobsSidebarContent = (props: Omit<CandidateJobsSidebarProps, 'isAuthenticated'>) => {
  const {
    locale,
    className,
    showAccountSettings = true,
    onSwitchContext,
    onOpenMap,
  } = props;
  const { t } = useTranslation('common');

  // Get navigation context
  const {
    currentView, navigateToNewJobSearchForm, navigateToCandidateSearch, navigateToKeywordSearch, navigateToEditSearch,
  } = useSidebarNavigation();

  const dispatchMobileClose = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      window.dispatchEvent(new Event('close-sidebar'));
      window.dispatchEvent(new Event('close-detail-modal'));
    }
  };

  const handleNavigateToNewJobSearchForm = () => { dispatchMobileClose(); navigateToNewJobSearchForm(); };
  const handleNavigateToAlert = (filters: CandidateSearchFilters) => { dispatchMobileClose(); navigateToCandidateSearch(filters); };
  const handleNavigateToKeywordSearch = (keyword: string, source: McKeywordSource) => { dispatchMobileClose(); navigateToKeywordSearch(keyword, source); };

  const extractFiltersFromAlert = (alert?: ApiSchema<'JobSearchAlertData'>): CandidateSearchFilters => {
    const jobSearch = alert?.jobSearch;
    if (!jobSearch) return {};
    return {
      ...(jobSearch.keyword && { keyword: jobSearch.keyword }),
      ...(jobSearch.location?.length && { locations: jobSearch.location }),
    };
  };

  // Fetch job search alerts
  const { data: allAlerts, isLoading } = useSwrJobSearchAlerts({ locale });

  // Fetch own resume detail for last experience and next jobs
  const { data: resumeData } = useSwrOwnResumeDetail({ locale });

  // Get last experience from experiences array (first element is most recent)
  const lastExperience = useMemo(() => {
    if (!resumeData?.experiences || resumeData.experiences.length === 0) {
      return null;
    }
    return resumeData.experiences[0];
  }, [resumeData]);

  // Show only first 5 active alerts (no pagination)
  const { visibleAlerts } = useMemo(() => {
    const alerts = allAlerts || [];
    // Filter to only show active alerts
    const activeAlerts = alerts.filter((alert) => alert.active === true);
    return {
      visibleAlerts: activeAlerts.slice(0, ALERTS_MAX),
      isEmpty: activeAlerts.length === 0,
    };
  }, [allAlerts]);

  // Build combined list of all cards: alerts + last experience + next jobs
  const allCards = useMemo(() => {
    const cards: Array<{
      id: string;
      type: McCardType;
      alert?: ApiSchema<'JobSearchAlertData'>;
      title?: string;
      keyword?: string;
      source?: McKeywordSource;
      location?: string;
    }> = [];

    // Add alerts
    visibleAlerts.forEach((alert) => {
      cards.push({
        id: `alert-${alert.id}`,
        type: MC_CARD_TYPE_ALERT,
        alert,
      });
    });

    // Add last experience
    if (lastExperience?.title) {
      cards.push({
        id: 'last-experience',
        type: MC_CARD_TYPE_LAST_EXPERIENCE,
        title: lastExperience.title,
        keyword: lastExperience.title,
        source: MC_KEYWORD_SOURCE_LAST_EXPERIENCE,
        location: lastExperience.location ?? undefined,
      });
    }

    // Add next jobs
    if (resumeData?.tagCloud) {
      resumeData.tagCloud.slice(0, 3).forEach((tag, index) => {
        if (tag.label) {
          cards.push({
            id: `next-job-${tag.escoId || index}`,
            type: MC_CARD_TYPE_NEXT_JOB,
            title: tag.label,
            keyword: tag.label,
            source: MC_KEYWORD_SOURCE_NEXT_JOB,
          });
        }
      });
    }

    return cards;
  }, [visibleAlerts, lastExperience, resumeData?.tagCloud]);

  // Derive the active candidateSearch keyword/locations from context for selection highlighting
  const activeCandidateSearch = useMemo(() => (
    typeof currentView === 'object' && 'candidateSearch' in currentView ? currentView.candidateSearch : undefined
  ), [currentView]);

  // Determine selected keyword search (for keyword cards like last experience / next job)
  const selectedKeyword = useMemo(() => (typeof currentView === 'object' && 'keyword' in currentView ? currentView.keyword : undefined), [currentView]);

  return (
    <div className={`flex h-full flex-col bg-surface ${className}`}>
      {/* Top content - scrollable */}
      <div className="scrollbar-hide flex-1 overflow-y-auto px-4 pb-4 pt-0">
        {/* Navigation Header - Links on top */}
        <div className="mb-6 flex flex-col gap-3 border-b border-input pb-4">
          {/* Switch Context - Segmented control: Hiring | Job search */}
          <MissionControlSwitch
            activeContext="candidate"
            hireLabel={t('mc.switch.hire')}
            jobSearchLabel={t('mc.switch.job.search')}
            onSwitch={onSwitchContext ?? (() => {})}
          />

          {/* Role-searched - Show new job search form in main content */}
          <button
            type="button"
            onClick={handleNavigateToNewJobSearchForm}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-ink transition-colors hover:bg-surface-200"
            data-test-id="sidebar-role-searched"
          >
            <PlusCircleIcon className="size-5 text-ink" />
            <span>{t('header.label.new.job') || 'New job'}</span>
          </button>
        </div>

        {/* Job Search Alerts Section */}
        <div className="mb-3">
          <h3 className="mb-3 text-sm font-semibold text-ink">
            {t('my-jobs.page.title')}
          </h3>

          {/* Combined list: Saved Alerts + Last Experience + Next Jobs */}
          <div className="flex flex-col gap-3">
            {/* Loading state */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <TailwindSpinner className="size-6 text-primary" />
              </div>
            )}

            {/* Empty state — show popular searches when nothing is available */}
            {allCards.length === 0 && !isLoading && (
              <>
                <p className="mb-1 text-xs text-ink-medium">
                  {t('user.popular.searches')}
                </p>
                {POPULAR_SEARCH_KEYS.slice(0, 6).map((key) => {
                  const keyword = t(key);
                  const isSelected = keyword === selectedKeyword;
                  return (
                    <MemoizedCompactJobAlertCard
                      key={key}
                      title={keyword}
                      type={MC_CARD_TYPE_NEXT_JOB}
                      locale={locale}
                      isSelected={isSelected}
                      onSelect={() => handleNavigateToKeywordSearch(keyword, MC_KEYWORD_SOURCE_NEXT_JOB)}
                      onOpenMap={onOpenMap}
                    />
                  );
                })}
              </>
            )}

            {/* All cards (alerts + last experience + next jobs) */}
            {allCards.map((card) => {
              const isSelected = card.type === MC_CARD_TYPE_ALERT
                ? activeCandidateSearch?.keyword === card.alert?.jobSearch?.keyword
                  && JSON.stringify(activeCandidateSearch?.locations) === JSON.stringify(card.alert?.jobSearch?.location)
                : card.keyword === selectedKeyword;

              const handleSelect = () => {
                if (card.type === MC_CARD_TYPE_ALERT && card.alert) {
                  const filters = extractFiltersFromAlert(card.alert);
                  handleNavigateToAlert(filters);
                } else if (card.keyword && card.source) {
                  handleNavigateToKeywordSearch(card.keyword, card.source);
                }
              };

              const handleEdit = () => {
                if (card.type === MC_CARD_TYPE_ALERT && card.alert) {
                  navigateToEditSearch(extractFiltersFromAlert(card.alert));
                } else if (card.keyword) {
                  navigateToEditSearch({ keyword: card.keyword });
                }
              };

              const editKeyword = card.type === MC_CARD_TYPE_ALERT
                ? card.alert?.jobSearch?.keyword
                : card.keyword;
              const editLocations = card.type === MC_CARD_TYPE_ALERT
                ? card.alert?.jobSearch?.location
                : undefined;

              let editHref: string;
              if (editLocations?.length) {
                const locationSlug = slugify(editLocations.join(','));
                const keywordSlug = editKeyword ? `/${slugify(editKeyword)}` : '';
                editHref = `${getI18nLinkHref(`/mc/job/list/location-${locationSlug}`, locale).replace(/%2C/g, ',')}${keywordSlug}?edit=1`;
              } else if (editKeyword) {
                editHref = `${getI18nLinkHref(`/mc/job/list/${slugify(editKeyword)}`, locale)}?edit=1`;
              } else {
                editHref = `${getI18nLinkHref('/mc/job/list', locale)}?edit=1`;
              }

              return (
                <MemoizedCompactJobAlertCard
                  key={card.id}
                  alert={card.alert}
                  title={card.title}
                  type={card.type}
                  locale={locale}
                  isSelected={isSelected}
                  location={card.location}
                  onSelect={handleSelect}
                  onEdit={handleEdit}
                  editHref={editHref}
                  onOpenMap={onOpenMap}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Account Settings Link - Bottom aligned, outside scroll area */}
      {showAccountSettings && (
        <div className="shrink-0 border-t border-input bg-surface px-4 py-3">
          <Anchor
            href="/account/settings"
            styling="none"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-200"
          >
            <Cog6ToothIcon className="size-5 text-ink" />
            <span>{t('account.settings.title') || 'Account Settings'}</span>
          </Anchor>
        </div>
      )}
    </div>
  );
};

/**
 * @description Modular job alerts list sidebar component for candidates.
 * Displays saved job search alerts with "New Job Search" card at top.
 * Shows a sign-in prompt when user is not authenticated.
 */
export const CandidateJobsSidebar = (props: CandidateJobsSidebarProps) => {
  const { isAuthenticated = false, ...restProps } = props;

  // Show authenticated content if user is logged in
  if (isAuthenticated) {
    return <CandidateJobsSidebarContent {...restProps} />;
  }

  return (
    <GuestJobsSidebar
      locale={restProps.locale}
      className={restProps.className}
      onSwitchContext={restProps.onSwitchContext}
      onOpenMap={restProps.onOpenMap}
    />
  );
};
